import api from "services/api";
import serveRequestErrors from "common/serve-request-errors";

import { Menu, MenuItem } from "@mui/material";
import { formatDate } from "common/calendar";
import AppTable, { AppTableCell, AppTableMoreIcon } from "components/app-table";
import AppSearchInput from "components/app-search-input";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import downloadIcon from "assets/images/download-black-icon.svg";
import removeIcon from "assets/images/remove-icon.svg";
import addIcon from "assets/images/add-icon.svg";
import sanitizeObject from "common/sanitize-object";
import COMMON from "common";
import AppEmptyState from "components/app-empty-state";
import { useDispatch } from "react-redux";
import { promptAlertMessage } from "store/slices/alert";
import ERRORS from "common/errors";
import validateFileSize from "common/validate-file-size";

const AppEmployeeDocumentTab = () => {
	const dispatch = useDispatch();
	const { id } = useParams();
	const [searchParams, setSearchParams] = useSearchParams();
	const memoSetSearchParams = useRef(setSearchParams);
	const paramsRef = useRef({
		page: parseInt(searchParams.get("page") || 1),
		sort: searchParams.get("sort") || "",
		tab: "Document",
	});
	const uploadInputRef = useRef();
	const [anchorEl, setAnchorEl] = useState(null);
	const selectedRowRef = useRef(null);
	const [data, setData] = useState({ page: 0, size: 10, total: 0, offset: 0, totalElements: 0, prev: false, next: false, items: [] });
	const isEmptyState = useMemo(() => COMMON.TABLE_REQUEST_STATUS.INSTANCE === data.status, [data.status]);

	const onHandleGetList = useCallback(async () => {
		let response = null;

		setData({ page: paramsRef.current.page, size: 10, total: 0, offset: 0, totalElements: 0, prev: false, next: false, items: [] });

		try {
			const payload = { ...paramsRef.current, size: 10, email: id };

			memoSetSearchParams.current(sanitizeObject(payload), { replace: true });

			payload.page = paramsRef.current.page - 1;

			response = await api.get.employee.employeeDocument(payload);
		} catch (error) {
			serveRequestErrors(error);
		}

		if (response) {
			setData((prev) => ({
				...prev,
				page: paramsRef.current.page,
				prev: !response.first,
				next: !response.last,
				items: response.content,
				total: response.totalPages,
				totalElements: response.totalElements,
				offset: response?.pageable?.offset || 0,
				status: response?.pageable,
			}));
		}
	}, [id]);

	const onHandleUploadFile = useCallback(() => {
		uploadInputRef.current.click();
	}, []);

	const onHandleChange = useCallback(
		async (event) => {
			let response = null;
			try {
				const file = event.target.files[0];
				const formData = new FormData();

				const isValidFileSize = validateFileSize(file);
				if(!isValidFileSize) return serveRequestErrors(ERRORS.FILE_SIZE);
				
				formData.append("file", file);
				formData.append("email", id);

				await api.post.employee.uploadEmployeeDocument(formData);
				response = true;
			} catch (error) {
				serveRequestErrors(error);
			}

			if (response) {
				onHandleGetList();
				dispatch(promptAlertMessage({ message: "File has been uploaded successfully" }));
				event.target.value = null;
			}
		},
		[id, onHandleGetList, dispatch]
	);

	const onHandleDownload = useCallback(() => {
		const file = selectedRowRef.current;
		if (!file) return;

		window.open(file.filePath, "_self");
		setAnchorEl(null);
	}, []);

	const onHandleDelete = useCallback(async () => {
		const file = selectedRowRef.current;
		if (!file) return;

		let response = null;
		try {
			const payload = {
				id: file.id,
			};

			await api.post.employee.deleteEmployeeDocument(payload);
			response = true;
		} catch (error) {
			serveRequestErrors(error);
		}

		if (response) {
			dispatch(promptAlertMessage({ message: "File has been deleted successfully" }));
			setAnchorEl(null);
			onHandleGetList();
		}
	}, [onHandleGetList, dispatch]);

	const onHandlePagination = (event) => {
		const control = event.currentTarget?.getAttribute("data-ctrl");

		if (control) {
			if (control === "prev") {
				if (paramsRef.current.page <= 1) return;
				paramsRef.current.page -= 1;
			} else {
				if (paramsRef.current.page >= data.total) return;
				paramsRef.current.page += 1;
			}
		} else {
			paramsRef.current.page = event.target.value;
		}

		onHandleGetList();
	};

	const onHandleSort = (id, order) => {
		paramsRef.current.sort = order ? id + "," + order : "";

		paramsRef.current.page = 1;

		onHandleGetList();
	};

	const onHandleTableActionMenu = (event, row) => {
		selectedRowRef.current = row.original;
		setAnchorEl(event.currentTarget);
	};

	const onHandleCloseActionMenu = () => {
		setAnchorEl(null);
	};

	const CreatedDateCell = useCallback(({ row }) => <AppTableCell left value={formatDate(row.original.createdDate)} />, []);

	const MenuViewCell = useCallback(({ row }) => {
		return <AppTableMoreIcon onClick={(event) => onHandleTableActionMenu(event, row)} />;
	}, []);

	const columns = useMemo(
		() => [
			{
				Header: "File Name",
				accessor: "fileName",
				disableSortBy: false,
			},
			{
				Header: "Uploaded By",
				accessor: "uploadedBy",
				disableSortBy: false,
			},
			{
				Header: "Uploaded Date",
				accessor: "createdDate",
				disableSortBy: false,
				Cell: CreatedDateCell,
			},
			{
				Header: "",
				accessor: "*",
				disableSortBy: true,
				Cell: MenuViewCell,
			},
		],
		[CreatedDateCell, MenuViewCell]
	);

	useEffect(() => {
		onHandleGetList();
	}, [onHandleGetList]);

	return (
		<>
			<AppSearchInput buttons={[{ label: "Upload", icon: addIcon, onClick: onHandleUploadFile }]} />
			{isEmptyState && <AppEmptyState title="No records found" description="You don’t have any records yet" disabledButton />}
			{!isEmptyState && (
				<Fragment>
					<AppTable columns={columns} pages={data} onHandlePagination={onHandlePagination} onHandleSort={onHandleSort} />

					<Menu
						classes={{ root: "app-table-menu" }}
						anchorEl={anchorEl}
						open={!!anchorEl}
						onClose={onHandleCloseActionMenu}
						anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
						transformOrigin={{ vertical: "top", horizontal: "right" }}
					>
						<MenuItem onClick={onHandleDownload}>
							<img className="app-table-menu__icon" src={downloadIcon} alt="Download" />
							Download
						</MenuItem>
						<MenuItem onClick={onHandleDelete}>
							<img className="app-table-menu__icon" src={removeIcon} alt="delete" />
							Delete
						</MenuItem>
					</Menu>
				</Fragment>
			)}
			<input type="file" name="file" accept="image/jpeg, image/jpg, image/png, application/pdf" hidden ref={uploadInputRef} onChange={onHandleChange} />
		</>
	);
};

export default AppEmployeeDocumentTab;
